import * as Sentry from '@sentry/react';
import { config } from '../../config';

function safeSentryCall(action: string, fn: () => void) {
  try {
    fn();
  } catch (error) {
    if (config.appEnv !== 'production') {
      console.warn(`[sentry] failed to ${action}`, error);
    }
  }
}

export function initializeSentry() {
  safeSentryCall('initialize', () => {
    if (!config.sentryDsn) {
      if (config.appEnv !== 'production') {
        console.warn('Sentry DSN not configured');
      }
      return;
    }

    Sentry.init({
      dsn: config.sentryDsn,
      environment: config.appEnv,
      release: `${config.version}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: config.appEnv === 'production' ? 0.1 : 1.0,
      // Session Replay
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.01, // 100% of sessions with errors
      sampleRate: 0.3,
      // Filter out known non-actionable errors by message pattern
      ignoreErrors: [
        // Stripe internal cleanup errors (element destroyed during navigation/theme change)
        'This Element has already been destroyed',
        // Dynamic import failures from stale chunks after deployment
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'error loading dynamically imported module',
      ],
      // Additional options
      beforeSend(event, hint) {
        // Filter out errors from browser extensions
        if (event.exception) {
          const error = hint.originalException;
          if (error && typeof error === 'object' && 'message' in error) {
            const message = String(error.message);
            if (
              message.includes('chrome-extension://') ||
              message.includes('moz-extension://') ||
              message.includes('safari-extension://')
            ) {
              return null;
            }
          }
        }
        return event;
      },
    });
  });
}

/**
 * Set the current user context in Sentry for error tracking.
 * Call this after the user logs in or when user data becomes available.
 */
export function setSentryUser(user: { id: string; email?: string }) {
  safeSentryCall('set user', () => {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  });
}

/**
 * Set the current team context in Sentry for error tracking.
 * Call this when a team is selected or changed.
 */
export function setSentryTeam(team: { teamId: string }) {
  safeSentryCall('set team', () => {
    Sentry.setTag('team_id', team.teamId);
  });
}

/**
 * Set the current plan/package context in Sentry for filtering.
 */
export function setSentryPlan(plan: { package: string }) {
  safeSentryCall('set plan', () => {
    Sentry.setTag('plan', plan.package);
  });
}

/**
 * Clear user context from Sentry (e.g., on logout).
 */
export function clearSentryUser() {
  safeSentryCall('clear user', () => {
    Sentry.setUser(null);
  });
}

/**
 * Clear team context from Sentry (e.g., on logout).
 */
export function clearSentryTeam() {
  safeSentryCall('clear team', () => {
    Sentry.setTag('team_id', null);
  });
}

/**
 * Clear plan context from Sentry (e.g., on logout).
 */
export function clearSentryPlan() {
  safeSentryCall('clear plan', () => {
    Sentry.setTag('plan', null);
  });
}

export { Sentry };
